import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import CustomDialog from 'components/CustomDialog';
import CustomTextField from 'components/Input/CustomTextField';
import HospitalOrInstitutions from 'components/Global/HospitalOrInstitutions';
import ClusterSelection from 'components/ClusterSelection';
import CustomButton from 'components/CustomInput/Button';
import { getClusterByWorkflow, produce } from 'utility/general';
import api from 'api/call';
import { APPLICANT } from 'utility/lookup';

const SLayout = styled.div``;

const SSection = styled.div`
    margin-top: 20px;
`;

const SRowLayout = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;

    & > div:nth-child(2) {
        padding-left: 20px;
    }
`;

const SEvenChild = styled.div`
    flex-grow: 1;
`;

const MemberDialog = ({
    title,
    isShow = true,
    onDialogCancel,
    onCreateSubmit,
    workflow = '',
    showFullName = false,
    disableHKU = false,
    disableCUHK = false,
    isDelegateDialog = false,
    restrictApplicant = false,
    disablePosition = false,
    disableCluster = false,
    isSIDelegation = false,
    disableClusterChange = false,
    disableInstitutionChange = false,
    selectedRow = {},
    onEditSubmit = (data) => null,
    isEdit = false,
}) => {
    const [dialogData, setDialogData] = useState(selectedRow);
    const [isEmailLoading, setEmailLoading] = useState(false);
    const [validation, setValidation] = useState({});
    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);
    const [allowClusters, setAllowClusters] = useState([]);
    const [allowClusterForHospital, setAllowClusterForHospital] = useState([]);

    useEffect(() => {
        if (isSIDelegation) {
            setAllowClusters([]);
            if (selectedRow?.cluster) {
                setAllowClusterForHospital([selectedRow?.cluster]);
            }
        } else if (!isDelegateDialog || isSIDelegation) {
            const clusters = getClusterByWorkflow(workflow, false);
            setAllowClusters(clusters);
            if (selectedRow?.cluster) {
                setAllowClusterForHospital([selectedRow?.cluster]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow, isDelegateDialog, isSIDelegation]);

    useEffect(() => {
        if (selectedRow?.cluster) {
            setAllowClusterForHospital([selectedRow?.cluster]);
        }
    }, [selectedRow]);

    useEffect(() => {
        if (isShow) {
            setDialogData(selectedRow);
        }

        setForceErrorDisplay(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow]);

    const onProfileLoad = async () => {
        if (!validation['email']) {
            return;
        }

        try {
            setEmailLoading(true);
            const res = await api.user.getUserByEmail(dialogData?.email ?? '', restrictApplicant ? APPLICANT : '');
            const { contact, firstName, surname, title, email } = res.data.result;

            const dataToUpdate = produce(dialogData, (draft) => {
                draft['contact'] = contact;
                // draft['institution'] = institution;
                draft['firstName'] = firstName;
                draft['surname'] = surname;
                draft['title'] = title;
                draft['email'] = email;
                // draft['cluster'] = cluster;
            });

            setDialogData(dataToUpdate); // set local component data
        } catch (error) {
            console.log('173307 LeadPrincipalinfo.jsx error', error);
        }

        setEmailLoading(false);
    };

    const onChange = (e) => {
        const { name, value } = e.target;

        // narrow the choice of hospital selection
        if (name === 'cluster') {
            setAllowClusterForHospital([value]);
        }

        setDialogData(
            produce((draft) => {
                draft[name] = value;

                if (name === 'cluster') {
                    delete draft.institution;
                }
            }),
        );
    };

    const onSubmit = async () => {
        setForceErrorDisplay(true);

        const isFormValid = Object.values(validation).every((item) => item);

        if (!isFormValid) {
            return;
        }

        try {
            setEmailLoading(true);

            await api.user.getUserByEmail(dialogData.email, restrictApplicant ? APPLICANT : '');

            if (isEdit) {
                onEditSubmit(dialogData);
            } else {
                await onCreateSubmit(dialogData, { isDelegateDialog }, () => {
                    setDialogData({});
                });
            }

            //reset dialog data when data is added
        } catch (error) {
            console.log('163745 MemberDialog.jsx error', error);
            // showDialog({
            //     description: INVALID_EMAIL_USER,
            //     disableCancelButton: true,
            // });
        }

        setEmailLoading(false);
    };

    const isFreeTextInHospital = () => {
        if (
            isDelegateDialog
            // && dialogData.cluster === 'OTHERS'
        ) {
            return true;
        }

        return false;
    };

    return (
        <SLayout>
            <CustomDialog
                title={title}
                isShow={isShow}
                loading={isEmailLoading}
                onCancel={() => {
                    // clear the input form
                    setDialogData({});
                    onDialogCancel();
                }}
                onConfirm={onSubmit}
            >
                <div style={{ display: 'flex', width: 500 }}>
                    <div style={{ flexGrow: 1, paddingRight: 20 }}>
                        <CustomTextField
                            label="Email"
                            name="email"
                            maxLength={255}
                            value={dialogData?.email}
                            disabled={isEmailLoading}
                            validators={['isRequired', 'isEmail']}
                            forceErrorDisplay={forceErrorDisplay}
                            isValidValue={(isValid) => {
                                setValidation(
                                    produce((draft) => {
                                        draft.email = isValid;
                                    }),
                                );
                            }}
                            onChange={onChange}
                            showErrorWhenOnBlur
                        />
                    </div>

                    <div>
                        <CustomButton
                            loading={isEmailLoading}
                            style={{ height: 37.6 }}
                            onClick={onProfileLoad}
                            disabled={!dialogData?.email}
                        >
                            Load Profile
                        </CustomButton>
                    </div>
                </div>
                <SRowLayout>
                    {showFullName ? (
                        <CustomTextField
                            label="Full Name"
                            name="fullName"
                            maxLength={255}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            isValidValue={(isValid) => {
                                setValidation(
                                    produce((draft) => {
                                        draft.fullName = isValid;
                                    }),
                                );
                            }}
                            value={dialogData?.fullName}
                            onChange={onChange}
                        />
                    ) : (
                        <>
                            <SEvenChild>
                                <CustomTextField
                                    label="Surname"
                                    name="surname"
                                    maxLength={255}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={['isRequired', 'disableChinese']}
                                    isValidValue={(isValid) => {
                                        setValidation(
                                            produce((draft) => {
                                                draft.surname = isValid;
                                            }),
                                        );
                                    }}
                                    value={dialogData?.surname}
                                    onChange={onChange}
                                />
                            </SEvenChild>
                            <SEvenChild>
                                <CustomTextField
                                    label="First Name"
                                    name="firstName"
                                    maxLength={255}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={['isRequired', 'disableChinese']}
                                    isValidValue={(isValid) => {
                                        setValidation(
                                            produce((draft) => {
                                                draft.firstName = isValid;
                                            }),
                                        );
                                    }}
                                    value={dialogData?.firstName}
                                    onChange={onChange}
                                />
                            </SEvenChild>
                        </>
                    )}
                </SRowLayout>
                <SRowLayout>
                    <CustomTextField
                        label="Title"
                        name="title"
                        maxLength={255}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['isRequired', 'disableChinese']}
                        isValidValue={(isValid) => {
                            setValidation(
                                produce((draft) => {
                                    draft.title = isValid;
                                }),
                            );
                        }}
                        value={dialogData?.title}
                        tooltip={'e.g. Dr/Mr/Mrs/Ms/Prof.'}
                        onChange={onChange}
                    />
                    {!disablePosition && (
                        <SEvenChild>
                            <CustomTextField
                                label="Position"
                                name="position"
                                maxLength={255}
                                forceErrorDisplay={forceErrorDisplay}
                                value={dialogData?.position}
                                validators={['isRequired']}
                                isValidValue={(isValid) => {
                                    setValidation(
                                        produce((draft) => {
                                            draft.position = isValid;
                                        }),
                                    );
                                }}
                                tooltip={
                                    'Please fill in the position relevant to the hospital/institution which you have selected below.'
                                }
                                onChange={onChange}
                            />
                        </SEvenChild>
                    )}
                </SRowLayout>
                <SRowLayout>
                    {!disableCluster && (
                        <SEvenChild>
                            <ClusterSelection
                                value={dialogData?.cluster ?? ''}
                                allowClusters={allowClusters}
                                disabled={disableClusterChange}
                                onChange={(e) => {
                                    onChange({
                                        target: {
                                            name: 'cluster',
                                            value: e.target.value,
                                        },
                                    });
                                }}
                            />
                        </SEvenChild>
                    )}
                    <SEvenChild>
                        <HospitalOrInstitutions
                            label="Hospital / Institution"
                            name="institution"
                            // isRequired
                            // validators={['isRequired', 'disableChinese']}
                            isAuthorizedEmail={!isFreeTextInHospital()}
                            disableHKU={disableHKU}
                            disableCUHK={disableCUHK}
                            forceErrorDisplay={forceErrorDisplay}
                            disabled={(!disableCluster && !dialogData.cluster) || disableInstitutionChange}
                            allowClusters={allowClusterForHospital}
                            validators={['isRequired']}
                            isValidValue={(isValid) => {
                                setValidation(
                                    produce((draft) => {
                                        draft.institution = isValid;
                                    }),
                                );
                            }}
                            hospitalOrInstitution={dialogData?.institution}
                            setHospitalOrInstitution={onChange}
                            maxLength={255}
                        />
                    </SEvenChild>
                </SRowLayout>
                <SSection>
                    <CustomTextField
                        label="Department"
                        name="department"
                        maxLength={255}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['isRequired']}
                        isValidValue={(isValid) => {
                            setValidation(
                                produce((draft) => {
                                    draft.department = isValid;
                                }),
                            );
                        }}
                        value={dialogData?.department}
                        onChange={onChange}
                    />
                </SSection>
            </CustomDialog>
        </SLayout>
    );
};

export default MemberDialog;
